import { Link } from 'react-router-dom';

import { ChevronRightIcon } from './icons.jsx';

// The app-style action panel — an icon tile, a bold title, a live status sub-line
// and a chevron — the web equivalent of the mobile app's ActionPanel. Used for the
// property-level actions, the zone-screen action list and the help section's
// lists so every surface is the same component (matching the app), not bespoke
// markup per screen.
//
// It is a router Link: pressing it opens the action's dedicated screen. The icon is
// decorative (the title is the label), so callers pass a plain glyph and the icon
// wrapper is aria-hidden. The sub-line is omitted entirely when empty rather than
// rendering a blank row.
//
// Without `icon` it is the app's ActionPanelNoIcon: no icon box, and a title that
// may wrap (the app's no-icon rows carry whole questions, which its one-line
// titles would cut short). `actionIcon` replaces the chevron, as the app's
// ActionIcon does — an outward arrow on a row that leaves for another site.
//
// With `href` instead of `to` it is an external link, opened in a new tab (the
// app's Linking.openURL) and saying so for assistive tech.
//
// `disabled` mirrors the app's ActionPanel `disabled`: the panel is greyed and
// does not navigate, but keeps its title and sub-line (the app dims the same text
// rather than hiding it). It is rendered as a non-navigating element that keeps
// the link role and carries aria-disabled, so assistive tech still reads "Plus
// Hours, link, dimmed" rather than losing the item. This is a CUE only — a
// browser user can still reach the screen by URL, so every destination keeps its
// own guard (the schedule editor's Auto lock, the boost screen's mode hint).
export default function ActionPanel({
  to,
  href,
  icon,
  title,
  sub,
  disabled = false,
  actionIcon,
}) {
  const body = (
    <>
      {icon ? (
        <span className="action-panel__icon" aria-hidden="true">
          {icon}
        </span>
      ) : null}
      <span className="action-panel__text">
        <span className="action-panel__title">{title}</span>
        {sub ? <span className="action-panel__sub">{sub}</span> : null}
      </span>
      <span className="action-panel__chevron" aria-hidden="true">
        {actionIcon ?? <ChevronRightIcon />}
      </span>
    </>
  );

  const className = icon
    ? 'action-panel'
    : 'action-panel action-panel--no-icon';

  if (disabled) {
    return (
      <span
        className={`${className} action-panel--disabled`}
        role="link"
        aria-disabled="true"
      >
        {body}
      </span>
    );
  }

  if (href) {
    return (
      <a
        href={href}
        className={className}
        target="_blank"
        rel="noopener noreferrer"
      >
        {body}
        <span className="visually-hidden"> (opens in a new tab)</span>
      </a>
    );
  }

  return (
    <Link to={to} className={className}>
      {body}
    </Link>
  );
}
